import { AuthError } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { mapAuthCodeToMessage } from '@/utils';
import { locations } from '@/locations';
import { FormattedText } from '@/components/uiKit/FormattedText';
import { useAuth } from '@/providers/AuthProvider';
import { useDispatch } from 'react-redux';
import { useLazyGetUsersQuery } from '@/store/api/userApi';
import { analytics } from '@/analytics';
import { setIsFirebaseRequesting, setMfaExpired } from '@/store/slices/applicationSlice';
import { useLazyGetStatusQuery } from '@/store/api/authApi';
import * as Sentry from '@sentry/react';
import { useToast } from '@/hooks/shared/use-toast';
import { BaseButton } from '@/components/uiKit/Button/BaseButton';
import { Input } from '@/components/uiKit/FormControl/Input/BaseInputNew';
import { PASSWORD_PATTERN } from '@/const';

type LoginFormData = {
  email: string;
  password: string;
};

export const LoginForm = () => {
  const [fetchUsers, { isLoading: isLoadingUsers, isFetching: isFetchingUsers }] = useLazyGetUsersQuery();
  const [fetchAuthStatus, { isLoading: isLoadingAuthStatus, isFetching: isFetchingAuthStatus }] =
    useLazyGetStatusQuery();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signIn } = useAuth();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    watch,
  } = useForm<LoginFormData>();

  const emailValue = watch('email');
  const passwordValue = watch('password');

  const onSubmit = async (values: LoginFormData) => {
    const { email, password } = values;
    console.log('isValid', isValid);

    if (isValid) {
      dispatch(setIsFirebaseRequesting(true));
      dispatch(setMfaExpired(false));

      try {
        const { user } = await signIn(email.trim(), password.trim());
        const isStrongPassword = PASSWORD_PATTERN.test(password.trim());

        if (user) {
          localStorage.removeItem('userMustChangePassword');
          localStorage.removeItem('afterMfaNavigateToPasswordResetPage');
          const authStatus = await fetchAuthStatus({}).unwrap();

          if (authStatus.run_mfa) {
            if (!isStrongPassword) {
              localStorage.setItem('afterMfaNavigateToPasswordResetPage', 'true');
            }
            navigate(locations.public.secondFactorMethod);
          } else {
            if (isStrongPassword) {
              const usersData = await fetchUsers({ uids: user.uid }).unwrap();
              const userData = usersData[0];
              if (userData) {
                analytics.identify(userData?.email, {
                  name: userData?.displayName,
                  firstName: userData?.first_name,
                  lastName: userData?.last_name,
                  email: userData?.email,
                  id: userData?.user_id,
                  role: userData?.role,
                });
              }
              navigate(locations.protected.sites.index);
            } else {
              localStorage.setItem('userMustChangePassword', 'true');
              navigate(locations.public.change);
            }
          }
        }
      } catch (error: unknown) {
        const authError = error as AuthError;
        Sentry.captureException(authError);
        console.log('authError', authError);

        toast({
          variant: 'destructive',
          title: mapAuthCodeToMessage(authError.code),
        });
      } finally {
        dispatch(setIsFirebaseRequesting(false));
      }
    }
  };

  const isLoading = isSubmitting || isLoadingUsers || isLoadingAuthStatus || isFetchingAuthStatus || isFetchingUsers;

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col space-y-8">
          <Input
            id="email"
            type="email"
            label={t('auth.login.form.email.label')}
            variant="filled"
            error={!!errors.email}
            helperText={errors.email?.message}
            value={emailValue || ''}
            {...register('email', {
              required: 'Email is required',
            })}
          />

          <Input
            id="password"
            type="password"
            label={t('auth.login.form.password.label')}
            variant="filled"
            error={!!errors.password}
            helperText={errors.password?.message}
            value={passwordValue || ''}
            {...register('password', {
              required: 'Password is required',
            })}
          />
        </div>

        <div className="flex flex-col-reverse md:flex-row mt-8 justify-between">
          <div className="w-full text-center">
            <BaseButton
              variant="ghost"
              className="text-[#7071f3] uppercase text-sm"
              onClick={() => navigate('/reset-password')}
            >
              <FormattedText label="auth.login.form.resetPassword" />
            </BaseButton>
          </div>

          <div className="w-full text-center mb-5 md:mb-0">
            <BaseButton
              type="submit"
              variant="filled"
              size="lg"
              className="w-4/5 md:w-full bg-[#7071f3] hover:bg-[#6061e3] uppercase text-sm rounded-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading...
                </span>
              ) : (
                <FormattedText label="auth.login.form.submit" />
              )}
            </BaseButton>
          </div>
        </div>
      </form>
    </div>
  );
};
